import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import { SuperHero } from "@bluebottlecoffee/design-system/components";
import { CanonicalHead } from "../../../components/CanonicalHead";
import { globalModulesProjection } from "../../../lib/sanity/global-modules";
import sanityClient from "../../../lib/sanity-client";
import renderModule from "../../../lib/render-module";
import { toSuperHeroProps } from "../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import {
  getDefaultStaticPaths,
  getDefaultStaticProps,
} from "../../../lib/utils";
import { OurStory } from "../../../lib/sanity-schema";
import { BaseProps } from "../../../lib/sanity/shared";
import { filters } from "../../../lib/sanity/utils";
import { resolveTabbedCollections } from "../../../lib/resolve-tabbed-collections";

interface PageData extends BaseProps {
  ourStory: OurStory;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);
  const { lang, region } = defaultProps;

  const ourStory = await sanityClient().fetch<OurStory>(`*[
    ${filters.whereType("ourStory")} &&
    ${filters.excludeDrafts}
  ][0]{
    ...,
    modules[]{
      ...,
      ${globalModulesProjection(lang)},
    }
  }`);

  await resolveTabbedCollections(ourStory?.modules, { lang, region });

  return {
    props: {
      ourStory,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: OurStory) {
  if (!data.title) {
    throw Error("Our Story page must have a title.");
  }
}

const OurStoryPage: FunctionComponent<PageData> = ({
  ourStory,
  ...layoutWrapperProps
}) => {
  validate(ourStory);
  const { region, lang, aria } = layoutWrapperProps;

  return (
    <>
      <CanonicalHead region={region} lang={lang} slug="our-story" />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,
          pageTitle: ourStory.title[lang],
          metaTags: ourStory.metaTags,
        }}
      >
        {ourStory.hero?.length > 0 && (
          <SuperHero {...toSuperHeroProps(ourStory.hero, aria, region, lang)} />
        )}
        {ourStory.modules?.length > 0 &&
          ourStory.modules.map((module) =>
            renderModule({ module, region, lang, aria }),
          )}
      </LayoutWrapper>
    </>
  );
};

export default OurStoryPage;
